import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {ESummaryPage} from '@/pages/components/audit-program/e-section/constants';
import {ES_PAGE} from '@/pages/components/audit-program/e-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface EsSummaryViewProps {
    header: AuditProgramSectionVO;
    page: ESummaryPage;
    /** Fallback copy when page fields are sparse (defaults to ES_PAGE). */
    fallback?: ESummaryPage;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 0.4, lineHeight: 1.25};
const bodySx = {...cellBorder, py: 0.5, px: 0.4, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** Es — Summary sheet for intangible assets objectives. */
export default function EsSummaryView({
    header,
    page,
    fallback = ES_PAGE,
    refCode = 'Es',
    values,
    onChange,
}: EsSummaryViewProps) {
    const meta = {
        title: page.title || fallback.title,
        objectivesTitle: page.objectivesTitle || fallback.objectivesTitle,
        objectives: page.objectives?.length ? page.objectives : fallback.objectives,
        planningTitle: page.planningTitle || fallback.planningTitle,
        finalTitle: page.finalTitle || fallback.finalTitle,
        materialityLabel: page.materialityLabel || fallback.materialityLabel,
        planningNote: page.planningNote || fallback.planningNote,
        finalBullets: page.finalBullets?.length ? page.finalBullets : fallback.finalBullets,
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1}}>
                {meta.objectivesTitle}
            </Typography>
            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 900, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 36}}/>
                        <col/>
                        <col style={{width: 110}}/>
                        <col style={{width: 120}}/>
                        <col style={{width: 90}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 80}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}>Objective</TableCell>
                            <TableCell sx={headSx} align="center">Key tests (planning)</TableCell>
                            <TableCell sx={headSx} align="center">Supp. procedures</TableCell>
                            <TableCell sx={headSx} align="center">Objectives met?</TableCell>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                            <TableCell sx={headSx} align="center">Initials</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {meta.objectives.map((obj) => (
                            <TableRow key={obj.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2">{obj.num}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" sx={{lineHeight: 1.4}}>{obj.text}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${obj.id}.keyTests`] ?? obj.defaultKeyTests ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.keyTests`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${obj.id}.suppRef`] ?? obj.defaultSuppRef ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.suppRef`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        placeholder="Yes/No"
                                        value={values[`${obj.id}.met`] ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.met`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        sx={fieldSx}
                                        value={values[`${obj.id}.comments`] ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.comments`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${obj.id}.initials`] ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.initials`, e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {meta.planningTitle}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1, maxWidth: 420}}>
                <Typography variant="body2" sx={{minWidth: 140}}>{meta.materialityLabel}</Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    sx={fieldSx}
                    value={values.materiality ?? ''}
                    onChange={(e) => onChange('materiality', e.target.value)}
                />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {meta.planningNote}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {meta.finalTitle}
            </Typography>
            <Typography variant="body2" sx={{mb: 0.5}}>
                From the audit work carried out I confirm that:
            </Typography>
            <Stack spacing={0.25} sx={{mb: 1.5, pl: 1}}>
                {meta.finalBullets.map((b, idx) => (
                    <Typography key={idx} variant="body2">- {b}</Typography>
                ))}
            </Stack>
            <InputField
                type="text"
                fullWidth
                compact
                multiline
                minRows={2}
                placeholder="Additional comments"
                value={values.finalComments ?? ''}
                onChange={(e) => onChange('finalComments', e.target.value)}
                sx={{maxWidth: 720}}
            />
        </Box>
    );
}
